import { distance } from '../core/math';
import { RivalDriver } from '../activities/rivalDriver';
import { planRoute } from './autopilot';
import { STREET_JOB, UNLOCK_STREET_JOBS } from './ids';
import type { MissionScript } from './runner';
import { holdHint } from './shared';
import type { MissionDef, RewardLine, StageDef, TargetRef } from './types';

/**
 * Street jobs: short, repeatable work from the phone box on Coalgate, unlocked by 'street-jobs'
 * (Borrowed Time). Lenny Pruett — "Lenny the Ear", who hears everything in Low Borough and passes it on
 * for a cut — rings with whatever's going. Eight kinds rotate (courier, quiet word, wheelman, two drops,
 * no fuss, debt chase, dry run, decoy) and each kind is a template filled from its own pool of places and
 * names, so the same kind comes round somewhere else, for someone else; `streetJob(n)` builds order n
 * from the save's count (the same n always builds the same job, so a retry is the same job).
 *
 * Pay stays inside £200–400 base (+ a small bonus line) whatever the kind: GAMEPLAY-ANALYSIS Appendix A.
 */

export const STREET_GIVER = 'phone-coalgate';
const CLIENT = 'Lenny the Ear';
export const STREET_KINDS = ['parcel', 'quiet-word', 'wheelman', 'two-drops', 'no-fuss', 'debt-chase', 'dry-run', 'decoy'] as const;
export type StreetKind = (typeof STREET_KINDS)[number];

const base = (title: string, tag: string, blurb: string, reward: number): Pick<MissionDef, 'id' | 'title' | 'client' | 'tag' | 'blurb' | 'reward' | 'requires' | 'requiresUnlock' | 'repeatable' | 'giver'> => ({
  id: STREET_JOB,
  title,
  client: CLIENT,
  tag: `STREET JOB · ${tag}`,
  blurb,
  reward,
  requires: [],
  requiresUnlock: UNLOCK_STREET_JOBS,
  repeatable: true,
  giver: { poi: STREET_GIVER, prompt: 'Answer the ringing phone (street job)', glyph: '☎' },
});

// ----------------------------------------------------------------------------- courier

/** Pick-up and drop pairs (~200 m apart) and the parcel clock. */
export const PARCEL_ROUTES: readonly { from: string; fromName: string; to: string; toName: string }[] = [
  { from: 'bank-savings', fromName: 'the Kessmouth Savings Bank', to: 'phone-tanners', toName: 'the phone box on Bell Street' },
  { from: 'st-brannocs', fromName: 'St. Brannoc’s Church', to: 'safehouse', toName: '9 Pilcher Street' },
  { from: 'gasworks-yard', fromName: 'the Gasworks Yard', to: 'garage-halloran', toName: 'Halloran Motors' },
  { from: 'shop-lamb-walk', fromName: 'Lamb Walk Market', to: 'safehouse', toName: '9 Pilcher Street' },
];
export const STREET_PARCEL_TIME = 120;

function parcelJob(variant: number): MissionDef {
  const r = PARCEL_ROUTES[variant % PARCEL_ROUTES.length]!;
  const late = 'Out of time: the 2 minutes ran out and the buyer went home. Try again with a car parked close by.';
  return {
    ...base('Hot Parcel', 'COURIER', `Lenny the Ear has a parcel to move. Collect it at ${r.fromName} and deliver it to ${r.toName} within 2 minutes, with no police following you. Pays £250, more if you’re early.`, 250),
    intro: [
      { who: CLIENT, text: `Lenny here. Parcel at ${r.fromName}, wants taking to ${r.toName}.` },
      { who: CLIENT, text: 'Two minutes once it’s in your hands. Don’t open it. Don’t shake it.' },
    ],
    introFocus: { poi: r.from },
    stages: [
      {
        id: 'pickup',
        kind: 'hold',
        at: { poi: r.from },
        radius: 5,
        seconds: 1.5,
        prompt: 'Take the parcel',
        text: `Collect the parcel at ${r.fromName}.`,
        hint: holdHint('Park a car close by: the 2-minute timer starts when you take it.'),
        onComplete: (m) => m.startTimer(STREET_PARCEL_TIME, late),
      },
      {
        id: 'drop',
        kind: 'goTo',
        at: { poi: r.to },
        radius: 8,
        requireHeat0: true,
        text: `Deliver the parcel to ${r.toName}.`,
        blockedText: 'The police are following you. Lose them first, then deliver.',
      },
    ],
    outroFocus: { poi: r.to },
    outro: [{ who: CLIENT, text: 'Lovely. Your money’s in the usual place, minus my bit.' }],
    payout: (m) => {
      const lines: RewardLine[] = [{ label: 'Delivery', amount: 250 }];
      const left = typeof m.vars.timeLeftAtEnd === 'number' ? m.vars.timeLeftAtEnd : 0;
      if (left > 50) lines.push({ label: 'Early', amount: 100 });
      return lines;
    },
  };
}

// ----------------------------------------------------------------------------- a quiet word

/** Where the late payer leaves his car. */
export const DEBTORS: readonly { at: string; name: string; who: string }[] = [
  { at: 'safehouse', name: 'Pilcher Street', who: 'Dougie Fenn' },
  { at: 'phone-mercy', name: 'Mercy Square', who: 'Arthur Lusk' },
  { at: 'respray-pike', name: 'Pike’s Respray', who: 'Big Malc' },
  { at: 'garage-kiln-motors', name: 'Kiln Road', who: 'Sonny Pratt' },
];
/** A dented car is a message received at this condition. */
export const QUIET_WORD_DENTED = 0.7;

function quietWordJob(variant: number): MissionDef {
  const d = DEBTORS[variant % DEBTORS.length]!;
  const near: TargetRef = { poi: d.at };
  return {
    ...base('A Quiet Word', 'PERSUASION', `${d.who} owes a friend of Lenny’s £60. Dent his blue Regent, parked on ${d.name}, with a bat or your car — no guns. Pays £200.`, 200),
    intro: [
      { who: CLIENT, text: `${d.who}. Owes sixty, says the cheque’s in the post. The post’s you.` },
      { who: CLIENT, text: `His Regent’s on ${d.name}. Dent it till he gets the message. A bat’s best; no shooters.` },
    ],
    introFocus: near,
    setup: (m) => {
      m.spawnVehicle('debtor', 'saloon', near, { locked: true, parked: true, paint: 0x2f4a6b });
    },
    onAttempt: (m) => {
      m.listen('weaponFired', () => m.fail('You fired a gun — this was meant to be a quiet warning. Try again: bat, crowbar or car only.'), 'attempt');
    },
    stages: [
      { id: 'find', kind: 'goTo', at: { vehicle: 'debtor' }, radius: 14, text: `Find ${d.who}’s blue Regent on ${d.name}.` },
      {
        id: 'dent',
        kind: 'custom',
        at: { vehicle: 'debtor' },
        radius: 4,
        text: 'Dent the blue Regent until the bar is full. A bat or crowbar is quickest; ramming works too.',
        hint: 'Fists won’t dent a car. There’s a free bat by the bandstand on Mercy Square; Sid (the revolver on the map) sells a cosh.',
        done: (m) => {
          const v = m.vehicle('debtor');
          return !v || v.destroyed || v.health / v.spec.maxHealth <= QUIET_WORD_DENTED;
        },
        progress: (m) => {
          const v = m.vehicle('debtor');
          return v ? (1 - v.health / v.spec.maxHealth) / (1 - QUIET_WORD_DENTED) : 1;
        },
        onEnter: (m) =>
          m.listen('weaponHit', (e) => {
            if (e.target === 'vehicle' && e.id === m.vehicle('debtor')?.id) m.vars.hits = (typeof m.vars.hits === 'number' ? m.vars.hits : 0) + 1;
          }),
        onComplete: (m) => {
          m.releaseVehicle('debtor');
          m.say([{ who: d.who, text: 'All right! All right! He’ll have it Friday!' }]);
        },
      },
      { id: 'away', kind: 'escapeHeat', text: 'Message delivered. Now get away and lose the police.', hint: 'Get out of their sight and stay hidden until your wanted level clears.' },
    ],
    outro: [{ who: CLIENT, text: `${d.who}’s found sixty quid down the back of the sofa. Funny, that.` }],
    payout: (m) => {
      const lines: RewardLine[] = [{ label: 'Persuasion', amount: 200 }];
      if (typeof m.vars.hits === 'number' && m.vars.hits >= 3) lines.push({ label: 'A proper craftsman', amount: 75 });
      return lines;
    },
  };
}

// ----------------------------------------------------------------------------- wheelman

/** Where the lads want dropping after the bank. */
export const WHEELMAN_DROPS: readonly { poi: string; name: string }[] = [
  { poi: 'lockup-pilcher-7', name: 'Lockup 7, Pilcher Ginnel' },
  { poi: 'lockup-arch-14', name: 'the Arch 14 lockup' },
  { poi: 'safehouse', name: '9 Pilcher Street' },
  { poi: 'lockup-coal-drop', name: 'Lockup 3, Coal Drop Lane' },
];
/** Seconds the engine has to keep running outside the bank (in a car, within this radius). */
export const WHEELMAN_WAIT = 8;
export const WHEELMAN_RADIUS = 14;

function waiting(m: MissionScript): boolean {
  const player = m.ctx.services.get('player');
  const bank = m.resolve({ poi: 'bank-savings' });
  return player.mode === 'driving' && Math.hypot(player.pos.x - bank.x, player.pos.z - bank.z) <= WHEELMAN_RADIUS;
}

function wheelmanJob(variant: number): MissionDef {
  const drop = WHEELMAN_DROPS[variant % WHEELMAN_DROPS.length]!;
  return {
    ...base('Wheelman', 'GETAWAY', `Three lads are robbing the Kessmouth Savings Bank and need a getaway driver. Wait outside in a car, lose the police, then drop them at ${drop.name}. Pays £400.`, 400),
    intro: [
      { who: CLIENT, text: 'Three lads, one bank, no driver. Their last one’s doing four years.' },
      { who: CLIENT, text: `Outside the Savings Bank, engine running. Then ${drop.name}, nice and calm.` },
    ],
    introFocus: { poi: 'bank-savings' },
    stages: [
      {
        id: 'bank',
        kind: 'goTo',
        at: { poi: 'bank-savings' },
        radius: 10,
        mode: 'vehicle',
        text: 'Drive a car to the Kessmouth Savings Bank and stop outside.',
        hint: 'Any car will do — you must be in it, driving.',
      },
      {
        id: 'engine',
        kind: 'custom',
        at: { poi: 'bank-savings' },
        radius: WHEELMAN_RADIUS,
        text: 'Wait outside the bank in the car. They won’t be long.',
        hint: 'Stay in the car, close to the bank, for 8 seconds. When the alarm rings, drive off.',
        blockedText: 'Go back to the bank and stay in the car!',
        blocked: (m) => !waiting(m),
        done: (m) => {
          const t = typeof m.vars.waited === 'number' ? m.vars.waited : 0;
          return t >= WHEELMAN_WAIT;
        },
        progress: (m) => (typeof m.vars.waited === 'number' ? m.vars.waited : 0) / WHEELMAN_WAIT,
        onTick: (m, dt) => {
          if (waiting(m)) m.vars.waited = (typeof m.vars.waited === 'number' ? m.vars.waited : 0) + dt;
        },
        onComplete: (m) => {
          m.say([
            { who: 'Alarm bell', text: 'BRRRRRRRING—', seconds: 1.6 },
            { who: 'One of the lads', text: 'Go, go, GO! Don’t just sit there!' },
          ]);
          m.setHeat(2);
        },
      },
      {
        id: 'getaway',
        kind: 'escapeHeat',
        text: 'The bank alarm has called the police. Lose them.',
        hint: 'Get out of their sight and stay hidden until your wanted level clears.',
        checkpoint: {
          label: 'the lads aboard, alarm ringing',
          restore: (m) => {
            m.spawnVehicle('getaway', 'saloon', { poi: 'bank-savings' });
            m.placePlayer({ vehicle: 'getaway' }, 'getaway');
            m.releaseVehicle('getaway');
            m.setHeat(2);
          },
        },
      },
      {
        id: 'drop',
        kind: 'goTo',
        at: { poi: drop.poi },
        radius: 10,
        mode: 'vehicle',
        requireHeat0: true,
        text: `Drop the lads at ${drop.name}.`,
        hint: 'Stay in the car until they’re out.',
        blockedText: 'They won’t get out while the police are after you. Lose them first.',
      },
    ],
    outroFocus: { poi: drop.poi },
    outro: [{ who: 'One of the lads', text: 'Not bad, driver. Not bad at all. Lenny’s got your end.' }],
  };
}

// ----------------------------------------------------------------------------- two drops

/** A parcel split in two: one pick-up, two doors, one clock. Legs of ~110–230 m. */
export const TWO_DROP_ROUTES: readonly { from: string; fromName: string; drops: readonly [{ poi: string; name: string }, { poi: string; name: string }] }[] = [
  { from: 'printworks-moss-yard', fromName: 'the old printworks in Moss’s Yard', drops: [{ poi: 'pub-brickmakers', name: 'the Brickmakers Arms' }, { poi: 'kiln-common', name: 'Kiln Common' }] },
  { from: 'docks-bonded-3', fromName: 'Bonded Warehouse No. 3', drops: [{ poi: 'phone-mercy', name: 'the phone box on Mercy Square' }, { poi: 'st-brannocs', name: 'St. Brannoc’s Church' }] },
  { from: 'gasworks-yard', fromName: 'the Gasworks Yard', drops: [{ poi: 'lockup-coal-drop', name: 'Lockup 3, Coal Drop Lane' }, { poi: 'kiln-goods-depot', name: 'the Kiln Road Goods Depot' }] },
];
export const TWO_DROP_TIME = 165;
/** Seconds left at the second door that count as early. */
export const TWO_DROP_EARLY = 50;

function twoDropsJob(variant: number): MissionDef {
  const r = TWO_DROP_ROUTES[variant % TWO_DROP_ROUTES.length]!;
  const [a, b] = r.drops;
  const late = 'Out of time: the 2 minutes 45 ran out before both parcels were delivered. Try again with a car parked close by.';
  return {
    ...base('Two Drops', 'COURIER', `Lenny the Ear has two parcels in one bag. Collect them at ${r.fromName}, then deliver one to ${a.name} and one to ${b.name} within 2 minutes 45, with no police following you. Pays £300, more if you’re early.`, 300),
    intro: [
      { who: CLIENT, text: `Two for the price of one. Bag’s at ${r.fromName}.` },
      { who: CLIENT, text: `First one to ${a.name}, second to ${b.name}. Don’t mix them up — they’re not the same sort of present.` },
    ],
    introFocus: { poi: r.from },
    stages: [
      {
        id: 'pickup',
        kind: 'hold',
        at: { poi: r.from },
        radius: 5,
        seconds: 1.5,
        prompt: 'Take the bag',
        text: `Collect the bag at ${r.fromName}.`,
        hint: holdHint('Park a car close by: the timer starts when you take it.'),
        onComplete: (m) => m.startTimer(TWO_DROP_TIME, late),
      },
      {
        id: 'drop-1',
        kind: 'goTo',
        at: { poi: a.poi },
        radius: 8,
        requireHeat0: true,
        text: `Deliver the first parcel to ${a.name}.`,
        hint: 'Stop by the marker. The second address comes next.',
        blockedText: 'The police are following you. Lose them first, then deliver.',
        onComplete: (m) => m.say([{ who: 'A voice at the door', text: 'About time. Go on, hop it.', seconds: 2.2 }]),
      },
      {
        id: 'drop-2',
        kind: 'goTo',
        at: { poi: b.poi },
        radius: 8,
        requireHeat0: true,
        text: `Deliver the second parcel to ${b.name}.`,
        blockedText: 'The police are following you. Lose them first, then deliver.',
      },
    ],
    outroFocus: { poi: b.poi },
    outro: [{ who: CLIENT, text: 'Both doors, both happy. Two for two, that’s what I like.' }],
    payout: (m) => {
      const lines: RewardLine[] = [{ label: 'Two deliveries', amount: 300 }];
      const left = typeof m.vars.timeLeftAtEnd === 'number' ? m.vars.timeLeftAtEnd : 0;
      if (left > TWO_DROP_EARLY) lines.push({ label: 'Early', amount: 100 });
      return lines;
    },
  };
}

// ----------------------------------------------------------------------------- no fuss

/** Who gets the quiet word, and where. Kept at wanted level 0 from the word onwards. */
export const NO_FUSS: readonly { at: string; name: string; who: string; word: string; reply: string }[] = [
  { at: 'pub-brickmakers', name: 'the Brickmakers Arms', who: 'Len Albery', word: 'Your cellar’s been noticed, Len. Move the barrels before Friday.', reply: 'Friday. Right. Tell him ta.' },
  { at: 'shop-lamb-walk', name: 'Lamb Walk Market', who: 'Queenie Barnes', word: 'The tallyman’s asking after your stall, Queenie. Pay him off quiet, and soon.', reply: 'That robbing so-and-so. All right, love. Thanks.' },
  { at: 'club-velvet-lamp', name: 'the Velvet Lamp', who: 'Maurice on the door', word: 'Your name came up in the wrong pub, Maurice. Keep your head down a fortnight.', reply: 'Did it now. I’ll be on my holidays, then.' },
  { at: 'st-brannocs', name: 'St. Brannoc’s Church', who: 'the verger', word: 'The lead off the roof’s been spotted at Gorringe’s. Somebody should get it back before the vicar does.', reply: 'Oh dear. Oh dear, oh dear. I’ll see to it.' },
];
/** Walk this far from the door afterwards and the job is done. */
export const NO_FUSS_LEAVE = 50;

function noFussJob(variant: number): MissionDef {
  const t = NO_FUSS[variant % NO_FUSS.length]!;
  const quiet = 'The police took an interest — this was meant to be quiet. Try again: no shooting, no crashes, no running from patrols.';
  return {
    ...base('No Fuss', 'A QUIET WORD', `Lenny the Ear needs a message passed to ${t.who} at ${t.name}, without anyone noticing. Go there, have a word and walk away — the police must not take an interest at any point. Pays £250.`, 250),
    intro: [
      { who: CLIENT, text: `A word in ${t.who}’s ear. Friendly. At ${t.name}.` },
      { who: CLIENT, text: 'No fuss, mind. Not so much as a copper looking twice at you, or it never happened.' },
    ],
    introFocus: { poi: t.at },
    stages: [
      {
        id: 'walk',
        kind: 'goTo',
        at: { poi: t.at },
        radius: 6,
        mode: 'foot',
        requireHeat0: true,
        text: `Go to ${t.name} and find ${t.who}. On foot for the last bit.`,
        hint: 'Leave the car round the corner and walk up.',
        blockedText: 'The police are after you. Lose them before you go anywhere near the door.',
      },
      {
        id: 'word',
        kind: 'hold',
        at: { poi: t.at },
        radius: 5,
        seconds: 2.5,
        prompt: 'Have a word',
        text: `Have a quiet word with ${t.who}.`,
        hint: holdHint('Stand by the door.'),
        onEnter: (m) => {
          // From the word onwards the job is over if the law so much as looks up.
          m.listen('heatChanged', (e) => {
            if (e.level > 0) m.fail(quiet);
          }, 'attempt');
          m.listen('weaponFired', () => m.fail(quiet), 'attempt');
        },
        onComplete: (m) => m.say([{ who: 'You', text: t.word }, { who: t.who, text: t.reply }]),
      },
      {
        id: 'leave',
        kind: 'custom',
        text: `Walk away from ${t.name}. No hurry, no fuss.`,
        hint: 'Get 50 metres from the door without drawing the police.',
        done: (m) => distance(m.ctx.services.get('player').pos, m.resolve({ poi: t.at })) >= NO_FUSS_LEAVE,
        progress: (m) => distance(m.ctx.services.get('player').pos, m.resolve({ poi: t.at })) / NO_FUSS_LEAVE,
        onComplete: (m) => {
          m.vars.leftOnFoot = m.ctx.services.get('player').mode === 'foot';
        },
      },
    ],
    outro: [{ who: CLIENT, text: 'Nobody saw a thing. Which is the point, isn’t it?' }],
    payout: (m) => {
      const lines: RewardLine[] = [{ label: 'A word passed', amount: 250 }];
      if (m.vars.leftOnFoot === true) lines.push({ label: 'Walked off like nothing happened', amount: 50 });
      return lines;
    },
  };
}

// ----------------------------------------------------------------------------- debt chase

/** A debtor who sees you coming and drives for it. `run` is where he's heading: if he gets there, he's gone. */
export const DEBT_RUNNERS: readonly { at: string; name: string; who: string; run: string; runName: string; paint: number }[] = [
  { at: 'garage-kiln-motors', name: 'Kiln Road Motors', who: 'Terry Mott', run: 'printworks-moss-yard', runName: 'Moss’s Yard', paint: 0x7a2f22 },
  { at: 'mercy-square', name: 'Mercy Square', who: 'Fat Vic', run: 'phone-dock', runName: 'the Dock Road', paint: 0x3b5a3a },
  { at: 'lockup-arch-14', name: 'the Arch 14 lockup', who: 'Les Coker', run: 'scrapyard-gorringe', runName: 'Gorringe’s scrap yard', paint: 0x6b5a2a },
];
/** The runner's car is "stopped" at this condition. */
export const DEBT_CHASE_DENTED = 0.75;
/** Rival-driver pace for the runner (m/s on the straights; corner factor). A saloon, not a racer. */
export const DEBT_RUNNER_CRUISE = 11;
/** Caught inside this many seconds: a bonus line. */
export const DEBT_CHASE_QUICK = 40;
/** Seen within this many metres, he's off. */
export const DEBT_SPOT_RADIUS = 28;

function debtChaseJob(variant: number): MissionDef {
  const d = DEBT_RUNNERS[variant % DEBT_RUNNERS.length]!;
  let driver: RivalDriver | null = null;
  const stopCar = (m: MissionScript): void => {
    const v = m.vehicle('runner');
    if (!v) return;
    v.controls.throttle = 0;
    v.controls.steer = 0;
    v.controls.handbrake = true;
    // He's out and gone on foot: an ordinary empty car from here on.
    v.controller = 'none';
    v.occupant = null;
    v.locked = false;
  };
  return {
    ...base('Debt Chase', 'COLLECTION', `${d.who} owes Lenny the Ear’s friend £90 and he’ll run when he sees you. Find his car at ${d.name}, then ram it until he gives up — before he reaches ${d.runName}. Pays £350.`, 350),
    intro: [
      { who: CLIENT, text: `${d.who}. Ninety quid, three months, and he’s seen your face, so he’ll leg it.` },
      { who: CLIENT, text: `His motor’s at ${d.name}. Don’t let him get to ${d.runName} — he’s got mates there.` },
    ],
    introFocus: { poi: d.at },
    setup: (m) => {
      driver = null;
      const v = m.spawnVehicle('runner', 'saloon', { poi: d.at }, { locked: true, paint: d.paint, controller: 'ai', occupant: { kind: 'ped', pedId: null } });
      v.controls.handbrake = true;
    },
    stages: [
      {
        id: 'find',
        kind: 'goTo',
        at: { vehicle: 'runner' },
        radius: DEBT_SPOT_RADIUS,
        text: `Find ${d.who}’s car at ${d.name}.`,
        hint: 'Come in a car: he won’t wait for you to walk.',
        onComplete: (m) => {
          const v = m.vehicle('runner');
          if (!v) return;
          const layout = m.ctx.services.get('layout');
          const line = [{ x: v.pos.x, z: v.pos.z }, ...planRoute(layout, v.pos, m.resolve({ poi: d.run }), false, 4, v.heading)];
          v.controls.handbrake = false;
          driver = new RivalDriver(m.ctx, v, line, DEBT_RUNNER_CRUISE, 0.9);
          m.say([{ who: d.who, text: 'Oh no you don’t—', seconds: 1.6 }]);
        },
      },
      {
        id: 'chase',
        kind: 'custom',
        at: { vehicle: 'runner' },
        radius: 6,
        text: `Ram ${d.who}’s car until he stops. Don’t let him reach ${d.runName}.`,
        hint: 'Hit it from the side or behind. A bat works too if you can catch him at the lights.',
        done: (m) => {
          const v = m.vehicle('runner');
          return !v || v.destroyed || v.health / v.spec.maxHealth <= DEBT_CHASE_DENTED;
        },
        progress: (m) => {
          const v = m.vehicle('runner');
          return v ? (1 - v.health / v.spec.maxHealth) / (1 - DEBT_CHASE_DENTED) : 1;
        },
        onTick: (m, dt) => {
          if (!driver) return;
          driver.drive(dt);
          if (driver.done) m.fail(`${d.who} got to ${d.runName} and his mates closed the gates. Try again: catch him sooner and hit harder.`);
        },
        onComplete: (m) => {
          m.vars.chaseTime = m.stageTime;
          driver = null;
          stopCar(m);
          m.releaseVehicle('runner');
          m.say([{ who: d.who, text: 'All right! ALL RIGHT! It’s in the glovebox — take it!' }]);
        },
      },
      { id: 'away', kind: 'escapeHeat', text: 'Money collected. Now get away and lose the police.', hint: 'Get out of their sight and stay hidden until your wanted level clears.' },
    ],
    // Blown or abandoned mid-chase: take his foot off the pedal (the runner leaves the car where it is).
    onResolved: (m, outcome) => {
      driver = null;
      if (outcome === 'failed') stopCar(m);
    },
    outro: [{ who: CLIENT, text: `Ninety quid, all there. ${d.who} says he’s taking the bus from now on.` }],
    payout: (m) => {
      const lines: RewardLine[] = [{ label: 'Debt collected', amount: 350 }];
      if (typeof m.vars.chaseTime === 'number' && m.vars.chaseTime <= DEBT_CHASE_QUICK) lines.push({ label: 'Caught him quick', amount: 50 });
      return lines;
    },
  };
}

// ----------------------------------------------------------------------------- dry run

/** A getaway route for next week's job, driven against the lads' time. Checkpoints are POIs. */
export const DRY_RUNS: readonly { stops: readonly { poi: string; name: string }[]; limit: number; par: number }[] = [
  { stops: [{ poi: 'bank-savings', name: 'the Kessmouth Savings Bank' }, { poi: 'phone-parade', name: 'the phone box on The Parade' }, { poi: 'lockup-arch-14', name: 'the Arch 14 lockup' }], limit: 95, par: 55 },
  { stops: [{ poi: 'pub-brickmakers', name: 'the Brickmakers Arms' }, { poi: 'kiln-common', name: 'Kiln Common' }, { poi: 'garage-kiln-motors', name: 'Kiln Road Motors' }, { poi: 'lockup-coal-drop', name: 'Lockup 3, Coal Drop Lane' }], limit: 110, par: 65 },
  { stops: [{ poi: 'club-velvet-lamp', name: 'the Velvet Lamp' }, { poi: 'mercy-square', name: 'Mercy Square' }, { poi: 'safehouse', name: '9 Pilcher Street' }], limit: 110, par: 65 },
];

function clock(s: number): string {
  return `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, '0')}`;
}

function dryRunJob(variant: number): MissionDef {
  const r = DRY_RUNS[variant % DRY_RUNS.length]!;
  const first = r.stops[0]!;
  const last = r.stops[r.stops.length - 1]!;
  const seen = 'The police took an interest — a rehearsal is no good if the law has seen the route. Try again: fast, but no crashes near a patrol.';
  const stages: StageDef[] = [
    {
      id: 'start',
      kind: 'goTo',
      at: { poi: first.poi },
      radius: 10,
      mode: 'vehicle',
      requireHeat0: true,
      text: `Drive a car to ${first.name} and stop. The clock starts there.`,
      hint: 'Any car will do. The police must not be after you.',
      blockedText: 'The police are after you. Lose them first — this is a rehearsal.',
      onComplete: (m) => {
        m.startTimer(r.limit, `Too slow: the lads would be in the cells by now. Drive the route in under ${clock(r.limit)}.`);
        m.vars.t0 = m.ctx.state.time;
        m.say([{ who: 'One of the lads', text: 'Stopwatch is going. Go on, then!', seconds: 2 }]);
        m.listen('heatChanged', (e) => {
          if (e.level > 0) m.fail(seen);
        }, 'attempt');
      },
    },
  ];
  r.stops.slice(1).forEach((s, i) => {
    const lastLeg = i === r.stops.length - 2;
    stages.push({
      id: lastLeg ? 'finish' : `via-${i + 1}`,
      kind: 'goTo',
      at: { poi: s.poi },
      radius: 12,
      mode: 'vehicle',
      text: lastLeg ? `Finish at ${s.name}. The lads’ time is ${clock(r.par)}.` : `Drive past ${s.name}.`,
      hint: 'Fast, but no police: a crash near a patrol ends the rehearsal.',
      onComplete: lastLeg ? (m) => (m.vars.runTime = Math.round((m.ctx.state.time - (typeof m.vars.t0 === 'number' ? m.vars.t0 : 0)) * 10) / 10) : undefined,
    });
  });
  return {
    ...base('Dry Run', 'REHEARSAL', `Next week’s job needs a getaway route. Drive from ${first.name} to ${last.name} the lads’ way, against the clock, without the police noticing. Pays £200, more if you beat the lads’ time of ${clock(r.par)}.`, 200),
    intro: [
      { who: CLIENT, text: 'Some lads want their getaway route timed before they do it for real.' },
      { who: CLIENT, text: `Start at ${first.name}, finish at ${last.name}. Quick, and don’t let a copper clock you doing it.` },
    ],
    introFocus: { poi: first.poi },
    stages,
    outroFocus: { poi: last.poi },
    outro: [{ who: 'One of the lads', text: 'That’ll do. We’ll buy you a drink after. If there is an after.' }],
    resultLine: (m) => (typeof m.vars.runTime === 'number' ? `Route driven in ${clock(m.vars.runTime)} — the lads’ time is ${clock(r.par)}.` : 'Route driven.'),
    payout: (m) => {
      const lines: RewardLine[] = [{ label: 'Route timed', amount: 200 }];
      if (typeof m.vars.runTime === 'number' && m.vars.runTime <= r.par) lines.push({ label: 'Beat the lads’ time', amount: 150 });
      return lines;
    },
  };
}

// ----------------------------------------------------------------------------- decoy

/** Where the lookalike van waits, and where it goes when the law has had its fun. */
export const DECOYS: readonly { at: string; name: string; dump: string; dumpName: string }[] = [
  { at: 'gasworks-yard', name: 'the Gasworks Yard', dump: 'scrapyard-gorringe', dumpName: 'Gorringe’s scrap yard' },
  { at: 'printworks-moss-yard', name: 'Moss’s Yard', dump: 'respray-pike', dumpName: 'Pike’s Respray' },
  { at: 'phone-mercy', name: 'Mercy Square', dump: 'warehouse-gullet-wharf', dumpName: 'the Gullet Wharf warehouse' },
];
/** Seconds the police have to be kept busy (with them after the van). */
export const DECOY_BUSY = 35;
/** The Vanes' green: the lookalike is painted the same. */
export const DECOY_PAINT = 0x2f5a3c;

function decoyJob(variant: number): MissionDef {
  const d = DECOYS[variant % DECOYS.length]!;
  const busy = (m: MissionScript): boolean => m.ctx.state.heat > 0 && m.ctx.services.get('player').vehicle === m.vehicle('decoy');
  return {
    ...base('Decoy Run', 'DECOY', `The Vanes are moving something in their green van tonight, and Lenny the Ear has been paid to draw the police off. Take the lookalike van from ${d.name}, let the police see it by the police station, keep them busy for ${DECOY_BUSY} seconds, lose them and leave the van at ${d.dumpName}. Pays £400.`, 400),
    intro: [
      { who: CLIENT, text: 'Odd one, this. The Vanes are paying, so don’t ask me to like it.' },
      { who: CLIENT, text: `Green van at ${d.name}, same as theirs. Wave it under the coppers’ noses, give them a run, then lose it at ${d.dumpName}.` },
    ],
    introFocus: { poi: d.at },
    setup: (m) => {
      m.spawnVehicle('decoy', 'van', { poi: d.at }, { parked: true, paint: DECOY_PAINT });
    },
    protect: [{ vehicle: 'decoy', minHealth: 0.12, reason: 'The decoy van is wrecked — nobody will chase a burning van. Try again and keep it in one piece.' }],
    stages: [
      { id: 'van', kind: 'enterVehicle', vehicle: 'decoy', text: `Get into the green van at ${d.name}.` },
      {
        id: 'show',
        kind: 'goTo',
        at: { poi: 'police-station' },
        radius: 22,
        vehicle: 'decoy',
        text: 'Drive the van past the police station so they see it.',
        hint: 'Drive past slowly. They’ll take it from there.',
        onComplete: (m) => {
          m.setHeat(2);
          m.say([{ who: 'Police radio', text: 'Green van, Wharf Road end. That’s the Vane van — all units!', seconds: 2.6 }]);
        },
      },
      {
        id: 'busy',
        kind: 'custom',
        text: `Keep the police chasing the van for ${DECOY_BUSY} seconds.`,
        hint: 'Stay in the van and keep moving. If they lose you too soon, drive past a patrol car again.',
        blocked: (m) => !busy(m),
        blockedText: (m) => (m.ctx.services.get('player').vehicle !== m.vehicle('decoy') ? 'Get back in the green van.' : 'They’ve lost you too soon. Drive past a patrol car so they see the van again.'),
        done: (m) => (typeof m.vars.busy === 'number' ? m.vars.busy : 0) >= DECOY_BUSY,
        progress: (m) => (typeof m.vars.busy === 'number' ? m.vars.busy : 0) / DECOY_BUSY,
        onTick: (m, dt) => {
          if (busy(m)) m.vars.busy = (typeof m.vars.busy === 'number' ? m.vars.busy : 0) + dt;
        },
        checkpoint: {
          label: 'in the van, the police on your tail',
          restore: (m) => {
            m.spawnVehicle('decoy', 'van', { poi: 'police-station' }, { paint: DECOY_PAINT });
            m.placePlayer({ vehicle: 'decoy' }, 'decoy');
            m.setHeat(2);
          },
        },
      },
      { id: 'lose', kind: 'escapeHeat', text: 'That’s long enough. Lose the police.', hint: 'Get out of their sight and stay hidden until your wanted level clears.' },
      {
        id: 'dump',
        kind: 'goTo',
        at: { poi: d.dump },
        radius: 10,
        vehicle: 'decoy',
        requireHeat0: true,
        text: `Leave the van at ${d.dumpName}.`,
        blockedText: 'The police are after you again. Lose them first.',
      },
    ],
    outroFocus: { poi: d.dump },
    outro: [{ who: CLIENT, text: 'The Vanes’ van got through clean. They owe us one. I’ll make sure they remember it.' }],
  };
}

/** Street job number `n` (from the save's count): the kind rotates every job, the place every round. */
export function streetJob(n: number): MissionDef {
  const k = ((n % STREET_KINDS.length) + STREET_KINDS.length) % STREET_KINDS.length;
  const variant = Math.floor(Math.max(0, n) / STREET_KINDS.length);
  const kind = STREET_KINDS[k]!;
  if (kind === 'parcel') return parcelJob(variant);
  if (kind === 'quiet-word') return quietWordJob(variant);
  if (kind === 'wheelman') return wheelmanJob(variant);
  if (kind === 'two-drops') return twoDropsJob(variant);
  if (kind === 'no-fuss') return noFussJob(variant);
  if (kind === 'debt-chase') return debtChaseJob(variant);
  if (kind === 'dry-run') return dryRunJob(variant);
  return decoyJob(variant);
}
